import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

import { graphql, useStaticQuery } from 'gatsby';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS, INSIGHTS_PAGE_URL } from '../../../constants';
import { Insight } from '../../../templates/InsightPage';
import { BackgroundColorTheme, ModuleBgColor } from '../../../templates/Page';
import { sortBy } from '../../../utils/nodash';
import { formatDate } from '../../../utils/projectUtils';
import { localize } from '../../../utils/sanity';
import { clsx, urlJoin } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Card from '../Card';
import ModuleLayout from '../ModuleLayout';
import TextLink from '../TextLink';
import * as styles from './LatestInsightsModule.module.scss';

export const LatestInsightsModuleFragment = graphql`
  fragment LatestInsightsModule on SanityPageLatestInsightsModule {
    title {
      ...LocaleStringFragment
    }
    insightsToUse
    insights {
      ...Insight
    }
    backgroundColor
    buttonText {
      ...LocaleStringFragment
    }
  }
`;

export type LatestInsightsModuleProps = {
  title: string;
  buttonText?: string;
  className?: string;
  backgroundColor: BackgroundColorTheme | '#FFFFFF';
} & (
  | {
      insightsToUse: 'latestThree';
      insights?: never;
    }
  | {
      insightsToUse: 'chooseManually';
      insights: Array<Insight>;
    }
);

interface QueryData {
  allSanityInsight: {
    nodes: Array<Insight>;
  };
}

export function getModuleBgColor(props: LatestInsightsModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return props.backgroundColor === '#FFFFFF' ? 'white' : 'colored';
}

function LatestInsightsModule(
  props: LatestInsightsModuleProps & CommonModuleProps,
): React.ReactElement {
  const queryData = useStaticQuery<QueryData>(graphql`
    {
      allSanityInsight {
        nodes {
          ...Insight
        }
      }
    }
  `);

  const {
    title,
    insightsToUse,
    insights,
    buttonText,
    className,
    backgroundColor,
    previousModuleBgColor,
    moduleId,
  } = props;

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(queryData, langs);

  const allInsightsSorted = sortBy(
    localizedData.allSanityInsight?.nodes,
    insight => insight._createdAt,
    'desc',
  );

  const containerRef = useRef<HTMLElement | null>(null);

  const insightsToShow =
    insightsToUse === 'chooseManually' ? insights : allInsightsSorted.slice(0, 3);

  useGSAP(
    () => {
      if (insightsToShow.length < 1) {
        return; // avoid errors on console
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.contentContainer}`,
          start: 'top 85%',
        } satisfies ScrollTrigger.Vars,
      });

      tl.from(`.${styles.card}`, {
        x: 40,
        autoAlpha: 0,
        stagger: 0.2,
        duration: 0.6,
      });
    },
    { scope: containerRef },
  );

  return insightsToShow.length > 0 ? (
    <ModuleLayout
      id={moduleId}
      moduleRef={containerRef}
      className={clsx(
        className,
        styles.container,
        backgroundColor === '#FFFFFF' && styles.whiteBackground,
        backgroundColor === '#E5F8E8' && styles.greenBackground,
        backgroundColor === '#EFECFA' && styles.purpleBackground,
      )}
      contentClassName={clsx(styles.contentContainer)}
      childrenClassName={styles.contentWrapper}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
      title={title}
    >
      <div className={styles.cardsContainer}>
        {insightsToShow.map((insight, i) => (
          <React.Fragment key={i}>
            <Card
              className={styles.card}
              title={insight.title}
              superTitle={`${formatDate(insight._createdAt, i18n.language)}
                `}
              text={'extract' in insight ? insight.extract : undefined}
              image={insight.image}
              link={{
                linkType: 'internalLink',
                internalLink: {
                  pageReference: { __typename: 'SanityInsight', slug: insight.slug },
                },
                text: t('readMoreButton.title', 'Ler Mais'),
              }}
            />
          </React.Fragment>
        ))}
      </div>
      <TextLink to={urlJoin(INSIGHTS_PAGE_URL)} withArrowIcon className={styles.link}>
        {buttonText || t('latest_insights_module.see_all_articles', 'Ver todos os artigos')}
      </TextLink>
    </ModuleLayout>
  ) : (
    <></>
  );
}

export default LatestInsightsModule;
